/* NVN Marine — facet rail renderer
 *
 * Renders the filter sidebar from an ES /search response's meta.facets.
 * Emits a `nvn:facets-change` CustomEvent with the merged filter state on
 * every checkbox/input change. The page orchestrator listens, mutates URL
 * state, and re-queries ES.
 *
 * Reuses existing CSS: .filter-rail, .filter-group, .filter-check, etc.
 * (see assets/kit.css).
 */
(function () {
  'use strict';

  var NVN = (window.NVN = window.NVN || {});

  // Loaded asynchronously by the orchestrator and passed to render().
  var tagDict = null;
  function setTagDict(dict) { tagDict = dict || null; }

  // Product-spec facets. `key` is both the URL/state key and the ES filter
  // param; `facet` is the bucket list in meta.facets. ES only returns buckets
  // for the categories in the result set (screen_sizes on chartplotters, etc.).
  var SPEC_FACETS = [
    { key: 'model', facet: 'trolling_motor_models', title: 'Model' },
    { key: 'voltage', facet: 'voltages', title: 'Voltage', numeric: true, suffix: 'V' },
    { key: 'thrust', facet: 'thrust_ratings', title: 'Thrust', numeric: true, suffix: ' lb' },
    { key: 'shaft_length', facet: 'shaft_lengths', title: 'Shaft Length', numeric: true, suffix: '"' },
    { key: 'screen_size', facet: 'screen_sizes', title: 'Screen Size', numeric: true, suffix: '"' },
    { key: 'gph', facet: 'gph_ratings', title: 'Flow Rate', numeric: true, suffix: ' GPH' },
    { key: 'speaker_size', facet: 'speaker_sizes', title: 'Speaker Size', numeric: true, suffix: '"' },
    { key: 'vhf_type', facet: 'vhf_types', title: 'Radio Type' },
    { key: 'amps', facet: 'amp_ratings', title: 'Output', numeric: true, suffix: 'A' },
    { key: 'banks', facet: 'bank_counts', title: 'Banks', numeric: true, suffix: '-Bank' },
    { key: 'watts', facet: 'wattages', title: 'Wattage', numeric: true, suffix: 'W' },
    { key: 'btu', facet: 'btu_ratings', title: 'Cooling Capacity', numeric: true, suffix: ' BTU' }
  ];
  var SPEC_KEYS = SPEC_FACETS.map(function (s) { return s.key; });

  function specLabel(spec, value) {
    var v = String(value);
    if (spec.key === 'vhf_type') return v.charAt(0).toUpperCase() + v.slice(1);
    if (spec.key === 'speaker_size' && /x/i.test(v)) return v;
    if (spec.key === 'btu') return Number(v).toLocaleString('en-US') + spec.suffix;
    return v + (spec.suffix || '');
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function tagInfo(key) {
    if (!tagDict) return { hidden: false, label: key, group: 'features' };
    var entry = tagDict.tags && tagDict.tags[key];
    if (entry) return { hidden: !!entry.hidden, label: entry.label || key, group: entry.group || 'features' };
    var patterns = tagDict.patterns || [];
    for (var i = 0; i < patterns.length; i++) {
      try {
        if (new RegExp(patterns[i].regex).test(key)) {
          return { hidden: !!patterns[i].hidden, label: patterns[i].label || key, group: patterns[i].group || 'features' };
        }
      } catch (_) {}
    }
    return { hidden: false, label: key, group: 'features' };
  }

  function groupLabel(id) {
    var groups = (tagDict && tagDict.groups) || [];
    for (var i = 0; i < groups.length; i++) if (groups[i].id === id) return groups[i].label;
    return id.charAt(0).toUpperCase() + id.slice(1);
  }

  function chev() {
    return '<span class="filter-group__right">' +
      '<svg class="filter-group__caret" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>' +
      '</span>';
  }

  function checkbox(name, value, label, count, checked) {
    var lbl = esc(label);
    var v = esc(value);
    var cnt = count != null ? '<span class="filter-check__count">' + count + '</span>' : '';
    return '<label class="filter-check' + (checked ? ' is-active' : '') + '">' +
      '<span class="filter-check__box">' +
        '<input type="checkbox" name="' + esc(name) + '" value="' + v + '"' +
        (checked ? ' checked' : '') + ' data-facet-input>' +
        '<svg class="filter-check__tick" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
      '</span>' +
      '<span class="filter-check__label">' + lbl + '</span>' +
      cnt +
    '</label>';
  }

  function detailsBlock(title, badge, body, openByDefault) {
    return '<details class="filter-group"' + (openByDefault ? ' open' : '') + '>' +
      '<summary class="filter-group__summary"><span>' + esc(title) + '</span>' +
      (badge ? '<span class="filter-group__badge">' + badge + '</span>' : '') +
      chev() + '</summary>' +
      '<div class="filter-group__body">' + body + '</div>' +
    '</details>';
  }

  // Wrap a list of checkbox HTML strings with a "Show all (N)" toggle when
  // there are more entries than the preview cap. Selected entries are pinned
  // to the top so they're always visible regardless of count rank.
  function truncatedList(rows, cap) {
    if (rows.length <= cap) return rows.join('');
    var visible = rows.slice(0, cap).join('');
    var hidden = rows.slice(cap).join('');
    return visible +
      '<div data-facet-overflow hidden>' + hidden + '</div>' +
      '<button type="button" class="filter-show-more" data-facet-show-more>' +
        '<span class="more">Show all (' + rows.length + ')</span>' +
        '<span class="less" hidden>Show less</span>' +
      '</button>';
  }

  function activeListSize(active, key) {
    var v = active[key];
    if (!v) return 0;
    if (Array.isArray(v)) return v.length;
    return String(v).split(',').filter(Boolean).length;
  }

  function isChecked(active, name, value) {
    var v = active[name];
    if (!v) return false;
    var list = Array.isArray(v) ? v : String(v).split(',');
    return list.indexOf(String(value)) !== -1;
  }

  // Free-text searches currently get facets for the whole catalog rather than
  // the results, which would put pump and A/C filters on a "vhf radio" search.
  // Only trust spec buckets when the in-stock split adds up to the result total.
  // The split ignores its own filter, so with in-stock checked only "true" counts.
  function facetsMatchResults(facets, active, opts) {
    var s = facets.in_stock;
    if (!s || opts.total == null) return true;
    var inStockOnly = active.in_stock === 'true' || active.in_stock === true;
    return (s['true'] || 0) + (inStockOnly ? 0 : s['false'] || 0) === opts.total;
  }

  function showSpecFacet(spec, facets, active, opts) {
    if (activeListSize(active, spec.key)) return true;
    return !!(facets[spec.facet] || []).length && facetsMatchResults(facets, active, opts);
  }

  function renderSpecFacet(spec, facets, active, opts) {
    if (!showSpecFacet(spec, facets, active, opts)) return '';
    var buckets = (facets[spec.facet] || []).slice();
    // Keep selected values listed even when other filters zero them out.
    var selected = active[spec.key] || [];
    selected.forEach(function (v) {
      var present = buckets.some(function (b) { return String(b.value) === v; });
      if (!present) buckets.push({ value: v, count: 0 });
    });
    buckets.sort(function (a, b) {
      var aChecked = isChecked(active, spec.key, a.value) ? 1 : 0;
      var bChecked = isChecked(active, spec.key, b.value) ? 1 : 0;
      if (aChecked !== bChecked) return bChecked - aChecked;
      // parseFloat puts oval 6x9 speakers next to the 6" rounds.
      return spec.numeric ? (parseFloat(a.value) - parseFloat(b.value)) || String(a.value).localeCompare(String(b.value)) : b.count - a.count;
    });
    var rows = buckets.map(function (b) {
      return checkbox(spec.key, b.value, specLabel(spec, b.value), b.count, isChecked(active, spec.key, b.value));
    });
    return detailsBlock(spec.title, activeListSize(active, spec.key) || '', truncatedList(rows, 8), true);
  }

  /**
   * Render facet rail.
   * @param {object} facets - meta.facets from ES response
   * @param {object} active - current filter state { vendor: [...], tags: [...], price_min, ... }
   * @param {object} opts   - { suppress: ['vendor'] } to hide implicit-collection facets
   * @returns {string} HTML
   */
  function render(facets, active, opts) {
    facets = facets || {};
    active = active || {};
    opts = opts || {};
    var suppress = opts.suppress || [];
    var totalActive = 0;
    ['vendor', 'product_type', 'tags', 'shipping', 'price_min', 'price_max', 'in_stock', 'on_sale'].concat(SPEC_KEYS).forEach(function (k) {
      if (active[k]) totalActive += activeListSize(active, k);
    });

    var html = '<div class="filter-rail-head">' +
      '<span class="filter-rail-title">Filters</span>' +
      (totalActive > 0 ? '<a href="#" class="filter-clear" data-facets-clear>Clear all (' + totalActive + ')</a>' : '') +
      '<button type="button" class="filter-rail-close" data-filter-close aria-label="Close filters">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
    '</div>';
    html += '<form data-facet-form>';

    // --- Availability / Deals (pinned first) ---
    // ES now sends in_stock/on_sale as { "true": n, "false": n }; older
    // responses used in_stock_count/on_sale_count.
    var inStockCount = facets.in_stock ? facets.in_stock['true'] || 0 : facets.in_stock_count;
    var onSaleCount = facets.on_sale ? facets.on_sale['true'] || 0 : facets.on_sale_count;
    var availBody = '';
    if (inStockCount != null) {
      availBody += checkbox('in_stock', 'true', 'Show Only In Stock', inStockCount,
        active.in_stock === 'true' || active.in_stock === true);
    }
    if (onSaleCount != null) {
      availBody += checkbox('on_sale', 'true', 'On sale', onSaleCount,
        active.on_sale === 'true' || active.on_sale === true);
    }
    if (availBody) html += detailsBlock('Availability', '', availBody, true);

    // --- Product specs ---
    SPEC_FACETS.forEach(function (spec) { html += renderSpecFacet(spec, facets, active, opts); });

    // --- Shipping (ES shipping_type keyword) ---
    // Render buckets in the exact order ES returns them (count-desc) and use the
    // ES value verbatim as both the checkbox value and label — no client-side
    // relabeling, so backend rules and the UI stay in sync. Products without a
    // shipping_type are in no bucket: zero checks = all show; any check = only
    // matching products (absents filtered out). No "Standard" pseudo-bucket.
    if (facets.shipping_type && facets.shipping_type.length) {
      var shipActive = activeListSize(active, 'shipping');
      var shipRows = facets.shipping_type.map(function (s) {
        return checkbox('shipping', s.value, s.value, s.count, isChecked(active, 'shipping', s.value));
      });
      html += detailsBlock('Shipping', shipActive || '', shipRows.join(''), true);
    }

    // --- Brand ---
    if (suppress.indexOf('vendor') === -1 && facets.vendors && facets.vendors.length) {
      var brandActive = activeListSize(active, 'vendor');
      // Pin checked brands to the top so they're always visible.
      var sortedBrands = facets.vendors.slice().sort(function (a, b) {
        var aChecked = isChecked(active, 'vendor', a.value) ? 1 : 0;
        var bChecked = isChecked(active, 'vendor', b.value) ? 1 : 0;
        if (aChecked !== bChecked) return bChecked - aChecked;
        return b.count - a.count;
      });
      var brandRows = sortedBrands.map(function (b) {
        return checkbox('vendor', b.value, b.value, b.count, isChecked(active, 'vendor', b.value));
      });
      html += detailsBlock('Brand', brandActive || '', truncatedList(brandRows, 8), true);
    }

    // --- Category (product_type) ---
    if (suppress.indexOf('product_type') === -1 && facets.product_types && facets.product_types.length) {
      var catActive = activeListSize(active, 'product_type');
      var sortedCats = facets.product_types.slice().sort(function (a, b) {
        var aChecked = isChecked(active, 'product_type', a.value) ? 1 : 0;
        var bChecked = isChecked(active, 'product_type', b.value) ? 1 : 0;
        if (aChecked !== bChecked) return bChecked - aChecked;
        return b.count - a.count;
      });
      // Group by left side of "|" for a two-level tree, preserving sort order
      var byParent = {};
      var parentOrder = [];
      sortedCats.forEach(function (pt) {
        var parts = String(pt.value).split('|').map(function (s) { return s.trim(); });
        var parent = parts.length > 1 ? parts[0] : '';
        var child = parts.length > 1 ? parts.slice(1).join(' | ') : parts[0];
        if (!byParent[parent]) { byParent[parent] = []; parentOrder.push(parent); }
        byParent[parent].push({ value: pt.value, label: child, count: pt.count });
      });
      var catRows = [];
      parentOrder.forEach(function (parent) {
        if (parent) catRows.push('<div class="filter-subhead">' + esc(parent) + '</div>');
        byParent[parent].forEach(function (item) {
          catRows.push(checkbox('product_type', item.value, item.label, item.count, isChecked(active, 'product_type', item.value)));
        });
      });
      html += detailsBlock('Category', catActive || '', truncatedList(catRows, 8), true);
    }

    // --- Price ---
    if (facets.price_ranges && facets.price_ranges.length) {
      var priceBody = '';
      facets.price_ranges.forEach(function (pr) {
        var label;
        if (pr.from == null) label = 'Under $' + pr.to;
        else if (pr.to == null) label = 'Over $' + pr.from;
        else label = '$' + pr.from + ' – $' + pr.to;
        var checked = String(active.price_min || '') === String(pr.from || '') &&
                      String(active.price_max || '') === String(pr.to || '');
        priceBody += '<label class="filter-check' + (checked ? ' is-active' : '') + '">' +
          '<span class="filter-check__box">' +
            '<input type="radio" name="price_range" value="' + esc((pr.from || '') + ':' + (pr.to || '')) + '"' +
            (checked ? ' checked' : '') + ' data-facet-input data-facet-price-bucket>' +
            '<svg class="filter-check__tick" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
          '</span>' +
          '<span class="filter-check__label">' + esc(label) + '</span>' +
          '<span class="filter-check__count">' + pr.count + '</span>' +
        '</label>';
      });
      var stats = facets.price_stats || {};
      priceBody += '<div class="filter-price" style="margin-top:8px">' +
        '<input name="price_min" placeholder="Min $" value="' + esc(active.price_min || '') + '" inputmode="numeric" class="filter-price-input" data-facet-input data-facet-price-input>' +
        '<span class="filter-price-sep">—</span>' +
        '<input name="price_max" placeholder="Max $" value="' + esc(active.price_max || '') + '" inputmode="numeric" class="filter-price-input" data-facet-input data-facet-price-input>' +
      '</div>';
      if (stats.min != null && stats.max != null) {
        priceBody += '<div class="filter-hint">Range: ' + (NVN.money ? NVN.money(stats.min) : '$' + stats.min) +
          ' – ' + (NVN.money ? NVN.money(stats.max) : '$' + stats.max) + '</div>';
      }
      html += detailsBlock('Price', '', priceBody, true);
    }

    // --- Named tags grouped by section ---
    if (facets.tags && facets.tags.length) {
      var groupedTags = {}; // groupId -> [{label, value, count}]
      facets.tags.forEach(function (t) {
        var info = tagInfo(t.value);
        if (info.hidden) return;
        var g = info.group || 'features';
        if (!groupedTags[g]) groupedTags[g] = [];
        groupedTags[g].push({ value: t.value, label: info.label, count: t.count });
      });
      Object.keys(groupedTags).forEach(function (gid) {
        var sorted = groupedTags[gid].slice().sort(function (a, b) {
          var aChecked = isChecked(active, 'tags', a.value) ? 1 : 0;
          var bChecked = isChecked(active, 'tags', b.value) ? 1 : 0;
          if (aChecked !== bChecked) return bChecked - aChecked;
          return b.count - a.count;
        });
        var rows = sorted.map(function (item) {
          return checkbox('tags', item.value, item.label, item.count, isChecked(active, 'tags', item.value));
        });
        html += detailsBlock(groupLabel(gid), '', truncatedList(rows, 6), false);
      });
    }

    html += '</form>';
    return html;
  }

  /**
   * Read the current state of a rendered facet form back to a filter object.
   */
  function readForm(root) {
    var state = { vendor: [], product_type: [], tags: [], shipping: [], in_stock: '', on_sale: '', price_min: '', price_max: '' };
    SPEC_KEYS.forEach(function (k) { state[k] = []; });
    if (!root) return state;
    root.querySelectorAll('[data-facet-input]').forEach(function (el) {
      var name = el.name;
      if (!name) return;
      if (el.type === 'checkbox') {
        if (!el.checked) return;
        if (Array.isArray(state[name])) state[name].push(el.value);
        else state[name] = el.value;
      } else if (el.type === 'radio') {
        if (!el.checked) return;
        if (name === 'price_range') {
          var parts = String(el.value).split(':');
          if (parts[0] !== '') state.price_min = parts[0];
          if (parts[1] !== '') state.price_max = parts[1];
        }
      } else {
        if (el.value !== '') state[name] = el.value;
      }
    });
    return state;
  }

  NVN.facets = { render: render, readForm: readForm, setTagDict: setTagDict, SPEC_FACETS: SPEC_FACETS, SPEC_KEYS: SPEC_KEYS, specLabel: specLabel };
})();
